import { Component } from '@theme/component';

/**
 * SDM — Tournée (sections/sdm-tournee.liquid): the dates of a concert tour, booked at the ticketing site; no day
 * or showtime to pick. Its styles: assets/sdm-tournee.css.
 *
 * - Data: the section renders the tour's dates as JSON, 250 per pagination page (Liquid's limit); the following
 *   pages come from the Section Rendering API (`?section_id=…&<page_param>=n`). Dates already past (Paris time)
 *   are dropped.
 * - List: every date in date order, month by month: the day, the town (and its country abroad), the venue, its
 *   labels (the date's mention, « Dernières places », « Reportée »), and its « Billets » button (or « Complet »,
 *   « Annulée », « Bientôt » without a link). A date opens its venue's sheet.
 * - Search: the field searches the list as one types: town, venue, postcode, country, day (« 20 mars »), accents
 *   and case aside, each word on its own. A search that names one town of the tour (« lyon », « 20 mars »): its
 *   dates first, then every other date from the nearest, as the crow flies. A town with no date (« Grenoble »): the
 *   State's address API (IGN Géoplateforme, France only) places it, and the list runs from the nearest date. A
 *   search that several towns match (« mars », « zénith »): the list is only filtered. The search and the map's
 *   button stay stuck under the header along the list; a new search brings the list's top back under them.
 * - « Voir la carte »: the sheet in the floating menu's glass, in its map view: every venue on the map (MapLibre,
 *   a theme asset, on OpenFreeMap's tiles, recoloured in the page's palette), framed on the whole tour, and every
 *   date in the panel. A pin or a date opens that venue's sheet (the map flies there): its dates, its site, the
 *   nearest other dates; « Toutes les dates » goes back to the map view. The map is made ready while the
 *   page is idle (MapLibre, its style, fonts and the tour's tiles in the browser's cache), so it opens drawn.
 * - « Me localiser » (the map's crosshair): the visitor's position (the browser asks them), a dot on the map, and
 *   every date from the nearest: in the list (« Près de vous », « Trier par date » to go back), in the map's panel,
 *   and a venue's distance in its sheet. Never kept in the address. « Recentrer » frames the tour, or the venue.
 * - Tone (`data-ton`): light, or dark; dark in midnight blue with `data-teinte="nuit"` (the page's tokens in
 *   assets/sdm-tournee.css, the map's colours here).
 * - The address keeps the state: `?ville=…` (the search), `&salle=…` (a venue's sheet) or `&carte=1` (the map).
 * - The header over the hero, in night ink once it is passed (`sdm-tournee-sur-clair` on <body>), the bubble towards
 *   another page, the products' island. A banner hero that carries the logotype (`data-logo-cache`): the header's
 *   logotype hidden at the top of the page, back in a fade from the first scroll (`sdm-tournee-defile` on <body>).
 * - Each « Billets » links to the date's ticketing page, with UTM parameters when the section asks for them. A
 *   click publishes `sdm_tournee_billetterie` to Shopify's customer events (for a custom pixel), pushes it to
 *   `dataLayer` when there is one, and dispatches `sdm-tournee:billetterie` on the element.
 *
 * @typedef {{ s: string, d: string, l: string, st: string, k: string, t: string, i: number, h: string }} TourDate
 * @typedef {{ id: string, n: string, a: string, p: string, v: string, c: string, y: number | null, x: number | null, w: string }} Venue
 * @typedef {{ name: string, lat: number, lng: number }} Place
 *
 * @typedef {object} Refs
 * @property {HTMLElement} hero
 * @property {HTMLElement} [produits]
 * @property {SVGDefsElement} [defs]
 * @property {HTMLElement} recherche
 * @property {HTMLFormElement} formulaire
 * @property {HTMLInputElement} champ
 * @property {HTMLButtonElement} effacer
 * @property {HTMLButtonElement} [voirCarte]
 * @property {HTMLElement} statut
 * @property {HTMLElement} resume
 * @property {HTMLElement} liste
 * @property {HTMLDialogElement} fiche
 * @property {HTMLElement} [plan]
 * @property {HTMLElement} ficheContenu
 * @property {HTMLAnchorElement} [bulle]
 * @property {HTMLElement} [cale]
 *
 * @extends {Component<Refs>}
 */
class SdmTournee extends Component {
  requiredRefs = ['recherche', 'champ', 'resume', 'liste', 'fiche', 'ficheContenu'];

  /** Dates to come, in date order. @type {TourDate[]} */
  #dates = [];

  /** @type {Map<string, Venue>} */
  #venues = new Map();

  /** Texts from the section's settings. @type {Record<string, string>} */
  #texts = {};

  /** Whether some of the tour's dates were dropped as past (then « over » rather than « coming soon »). */
  #hadPast = false;

  /** The search, as typed (trimmed). */
  #query = '';

  /** The town found for a search that matches no date, and the search it answers. @type {Place | null} */
  #place = null;
  #placeFor = '';

  /** The visitor's position (« Me localiser »), named « vous »; never kept in the address. @type {Place | null} */
  #here = null;

  /** Its dot on the map. @type {any} */
  #hereMarker = null;

  /** The note on the map (a position refused or not found), until it fades. */
  #noteTimer = 0;

  /** What the sheet shows: a venue (`fiche`), the whole tour (`carte`), or nothing (closed). */
  #view = '';

  /** The venue whose sheet is open. */
  #sheet = '';

  /** A sheet or the map asked for by the address, opened once its data is in. @type {{ view: string, id: string } | null} */
  #requested = null;

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {AbortController | undefined} */
  #search;

  #debounce = 0;

  /** @type {{ disconnect(): void }[]} */
  #observers = [];

  /** Whether more pages of dates are still loading. */
  #loading = false;

  /** Where the header's logos end (--st-collant, which depends on the screen's width). */
  #stuckAt = 0;

  /** The scroll's next frame, when one is asked for. */
  #frame = 0;

  /** The bubble's glide to or from its place, while it runs. @type {Animation | null} */
  #glide = null;

  /** The sheet's map (MapLibre), made at its first opening; its pins, by venue. @type {any} */
  #map = null;
  /** @type {Promise<void> | null} */
  #making = null;
  /** @type {Map<string, any>} */
  #pins = new Map();

  /** MapLibre and the map's style, loaded once. @type {Promise<object> | null} */
  #mapLoad = null;

  /** No map (no WebGL, MapLibre or the tiles out of reach): the sheet takes all the room. */
  #mapFailed = false;

  /** Whether the map's style has loaded, with the layer of the towns' names. */
  #mapNamed = false;

  /** Search strings, by date (its venue, its day). @type {Map<TourDate, string>} */
  #haystacks = new Map();

  /** The collection's products: kept here, since rendering the list detaches them when they sit in it. */
  /** @type {HTMLElement | undefined} */
  #products;

  connectedCallback() {
    super.connectedCallback();

    const first = parse(this.querySelector(DATA)?.textContent);
    if (!first) return;

    this.#abort = new AbortController();
    this.#products = this.#refs().produits;
    this.#merge(first);
    this.#readAddress();
    this.#listen();

    this.#refs().recherche.hidden = false;
    this.#refs().champ.value = this.#query;
    this.#syncClear();
    this.#render();
    this.#locateIfNeeded();
    this.#loadPages(first.pages, first.param);
    this.#initGlass();
    this.#watchVideos();
    this.#syncScroll();
    this.#warmMap();
    requestAnimationFrame(() => {
      this.#refs().bulle?.toggleAttribute('data-pret', true);
      this.#openRequested();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#search?.abort();
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#debounce);
    clearTimeout(this.#noteTimer);
    document.body.classList.remove('sdm-tournee-sur-clair', 'sdm-tournee-defile');
  }

  /** @returns {Refs} */
  #refs() {
    return /** @type {Refs} */ (this.refs);
  }

  /* ---------- data ---------- */

  /** @param {any} data - One page of the section's JSON. */
  #merge(data) {
    for (const [id, venue] of Object.entries(data.salles ?? {})) {
      this.#venues.set(id, { ...venue, id, c: venue.c ?? '', w: venue.w ?? '', y: number(venue.y), x: number(venue.x) });
    }
    if (data.textes) this.#texts = data.textes;

    const today = parisToday();
    const known = new Set(this.#dates.map(key));
    for (const date of data.dates ?? []) {
      if (!DAY.test(date.d ?? '') || !this.#venues.has(date.s) || known.has(key(date))) continue;
      if (date.d < today) {
        this.#hadPast = true;
        continue;
      }
      known.add(key(date));
      const st = date.st ?? '';
      this.#dates.push({ s: date.s, d: date.d, l: date.l ?? '', st, k: statusKind(st), t: date.t ?? '', i: 0, h: '' });
    }

    this.#dates.sort((a, b) => compare(a.d, b.d) || compare(this.#venue(a).v, this.#venue(b).v));
    this.#dates.forEach((date, index) => (date.i = index));
    this.#haystacks.clear();
  }

  /** @param {TourDate} date */
  #venue(date) {
    return /** @type {Venue} */ (this.#venues.get(date.s));
  }

  /**
   * Fetches pagination pages 2…n of the dates through the Section Rendering API.
   * @param {number} pages
   * @param {string} [param] - The page's parameter: not `page` for metaobjects (Liquid's `paginate.page_param`).
   */
  async #loadPages(pages, param = 'page') {
    if (!(pages > 1)) return;

    this.#loading = true;
    this.#render();
    const url = new URL(location.pathname, location.origin);
    if (this.dataset.vue) url.searchParams.set('view', this.dataset.vue);
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.searchParams.set('section_id', this.dataset.section ?? '');

    const requests = [];
    for (let page = 2; page <= pages; page++) {
      url.searchParams.set(param || 'page', String(page));
      requests.push(
        fetch(url, { signal: this.#abort?.signal })
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => parse(new DOMParser().parseFromString(html, 'text/html').querySelector(DATA)?.textContent))
          .catch(() => null)
      );
    }

    for (const data of await Promise.all(requests)) if (data) this.#merge(data);
    this.#loading = false;
    if (!this.isConnected) return;
    this.#render();
    this.#locateIfNeeded();
    this.#openRequested();
  }

  /** Venues with coordinates and dates to come: the map's pins. */
  #mapped() {
    const showing = new Set(this.#dates.map((date) => date.s));
    return [...this.#venues.values()].filter((venue) => venue.y !== null && venue.x !== null && showing.has(venue.id));
  }

  /* ---------- address ---------- */

  #readAddress() {
    const params = new URLSearchParams(location.search);
    this.#query = params.get('ville')?.trim() ?? '';
    const venue = params.get('salle');
    if (venue) this.#requested = { view: 'fiche', id: venue };
    else if (params.has('carte')) this.#requested = { view: 'carte', id: '' };
  }

  #openRequested() {
    const requested = this.#requested;
    if (!requested || this.#loading) return;
    this.#requested = null;
    if (requested.view === 'fiche') this.#openSheet(requested.id, { push: false });
    else this.#openMap({ push: false });
  }

  #writeAddress() {
    const url = new URL(location.href);
    for (const name of ['ville', 'salle', 'carte']) url.searchParams.delete(name);
    if (this.#query) url.searchParams.set('ville', this.#query);
    if (this.#view === 'fiche' && this.#sheet) url.searchParams.set('salle', this.#sheet);
    else if (this.#view === 'carte') url.searchParams.set('carte', '1');
    return url;
  }

  #replaceAddress() {
    history.replaceState(history.state, '', this.#writeAddress());
  }

  /* ---------- events ---------- */

  #listen() {
    const signal = this.#abort?.signal;
    const refs = this.#refs();

    refs.formulaire?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.#applyQuery({ scroll: true });
        if (matchMedia('(pointer: coarse)').matches) refs.champ.blur(); // closes a phone's keyboard
      },
      { signal }
    );
    refs.champ.addEventListener('input', () => this.#onInput(), { signal });
    refs.champ.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !refs.champ.value) return;
        event.preventDefault();
        this.#clearQuery();
      },
      { signal }
    );
    // On a phone, the search goes up under the header (it sticks there), the list under it above the keyboard; not
    // when it is stuck already.
    refs.champ.addEventListener(
      'focus',
      () => {
        if (!matchMedia('(pointer: coarse)').matches) return;
        setTimeout(() => {
          if (!this.hasAttribute('data-colle')) this.#toListTop(motion(), true);
        }, 250);
      },
      { signal }
    );
    refs.effacer?.addEventListener('click', () => this.#clearQuery(), { signal });
    refs.voirCarte?.addEventListener('click', () => this.#openMap(), { signal });

    // The map's script, as soon as the map's button or a date is pointed at: ready, or nearly, when it opens.
    const preload = (/** @type {Event} */ event) => {
      if (/** @type {HTMLElement} */ (event.target).closest?.('[data-fiche], .sdm-tournee__voir-carte')) {
        this.#loadMap().catch(() => {});
      }
    };
    for (const zone of [refs.liste, refs.voirCarte]) {
      zone?.addEventListener('pointerover', preload, { signal, passive: true });
      zone?.addEventListener('focusin', preload, { signal });
    }

    // Dates, sheets, the map's view and its buttons, in the list and its heading, the sheet and its map.
    for (const zone of [refs.liste, refs.resume, refs.ficheContenu, refs.plan]) {
      zone?.addEventListener('click', (event) => this.#onClick(event), { signal });
    }

    refs.fiche.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target === refs.fiche || target.closest('[data-fermer]')) refs.fiche.close();
      },
      { signal }
    );
    refs.fiche.addEventListener('close', () => this.#onSheetClose(), { signal });

    addEventListener(
      'popstate',
      () => {
        const params = new URLSearchParams(location.search);
        const id = params.get('salle');
        if (id && this.#venues.has(id)) this.#openSheet(id, { push: false });
        else if (params.has('carte')) this.#openMap({ push: false });
        else if (refs.fiche.open) refs.fiche.close();
      },
      { signal }
    );

    // The header follows the scroll: the window's on a phone, `.page-wrapper`'s on a computer (scroll events do
    // not bubble, but they are seen on the way down).
    const onScroll = () => {
      if (!this.#frame) this.#frame = requestAnimationFrame(() => this.#syncScroll());
    };
    document.addEventListener('scroll', onScroll, { signal, passive: true, capture: true });
    addEventListener(
      'resize',
      () => {
        this.#stuckAt = 0;
        onScroll();
      },
      { signal, passive: true }
    );

    // The page growing without a scroll (a search, the products' media) moves the bubble's place too.
    if (refs.bulle && 'ResizeObserver' in window) {
      const grown = new ResizeObserver(onScroll);
      grown.observe(this);
      this.#observers.push(grown);
    }

    // The sheet's map, resized with its box (the screen turned, the view changed).
    if (refs.plan && 'ResizeObserver' in window) {
      const resized = new ResizeObserver(() => {
        if (this.#map && refs.fiche.open) this.#map.resize();
      });
      resized.observe(refs.plan);
      this.#observers.push(resized);
    }
    this.#abort?.signal.addEventListener('abort', () => this.#map?.remove());
  }

  /**
   * What the scroll decides. The search sticks under the header's logos (assets/sdm-tournee.css) once it has
   * reached that line (`data-colle`: a filet under it). The hero is passed once its end goes under the logos; a
   * band of the rubric's colour then sits behind them, and the header turns to night ink, unless the page is dark.
   * Read by assets/sdm-tournee.css.
   */
  #syncScroll() {
    this.#frame = 0;
    const { hero, recherche } = this.#refs();
    if (!this.#stuckAt) this.#stuckAt = parseFloat(getComputedStyle(this).getPropertyValue('--st-collant')) || 100;
    const stuck = !recherche.hidden && recherche.getBoundingClientRect().top <= this.#stuckAt + 1;
    this.toggleAttribute('data-colle', stuck);
    const passed = stuck || !hero || hero.getBoundingClientRect().bottom <= this.#stuckAt;
    this.toggleAttribute('data-passe', passed);
    document.body.classList.toggle('sdm-tournee-sur-clair', passed && this.dataset.ton !== 'sombre');
    if (hero?.hasAttribute('data-logo-cache')) document.body.classList.toggle('sdm-tournee-defile', hero.getBoundingClientRect().top < -4);
    this.#syncBubble();
  }

  /**
   * The bubble towards another page: it floats on the floating menu's line until its place
   * (in the panel at the end of the page) comes up to that line, then is set there (`data-pose`) and glides to
   * it; it keeps off the search (`data-cache`). Its tone is the inverse of the ground under it.
   */
  #syncBubble() {
    const { bulle, cale, recherche } = this.#refs();
    if (!bulle || !cale) return;

    const floating = bulle.classList.contains('sdm-tournee__bulle--flottante');
    if (floating) {
      const line = innerHeight - (parseFloat(getComputedStyle(bulle).bottom) || 0);
      const set = cale.getBoundingClientRect().bottom <= line + 1;
      if (set !== bulle.hasAttribute('data-pose')) this.#setBubble(set);
      const search = recherche.hidden ? -Infinity : recherche.getBoundingClientRect().bottom;
      bulle.toggleAttribute('data-cache', search > line - bulle.offsetHeight - 16);
    }

    const dark = this.dataset.ton === 'sombre';
    let tone;
    if (!floating || bulle.hasAttribute('data-pose')) {
      const place = /** @type {HTMLElement | null} */ (cale.closest('[data-fond]'));
      tone = (place?.dataset.fond ?? (dark ? 'sombre' : 'clair')) === 'sombre' ? 'clair' : 'sombre';
    } else if (this.#glide) {
      return; // gliding: read at its end
    } else {
      const box = bulle.getBoundingClientRect();
      const under = toneAt(box.left + box.width / 2, box.top + box.height / 2, bulle) ?? (dark ? 'sombre' : 'clair');
      tone = under === 'sombre' ? 'clair' : 'sombre';
    }
    if (bulle.dataset.ton !== tone) bulle.dataset.ton = tone;
  }

  /** @param {boolean} set */
  #setBubble(set) {
    const { bulle } = this.#refs();
    if (!bulle) return;
    const first = bulle.getBoundingClientRect();
    bulle.toggleAttribute('data-pose', set);
    if (motion() === 'auto' || !bulle.hasAttribute('data-pret')) return;
    const last = bulle.getBoundingClientRect();
    const x = first.left - last.left;
    const y = first.top - last.top;
    if (Math.abs(x) < 1 && Math.abs(y) < 1) return;
    this.#glide?.cancel();
    const glide = bulle.animate([{ transform: `translate(${x}px, ${y}px)` }, { transform: 'none' }], {
      duration: 360,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    });
    this.#glide = glide;
    glide.finished
      .then(() => {
        if (this.#glide !== glide) return;
        this.#glide = null;
        this.#syncBubble();
      })
      .catch(() => {});
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);

    const ticket = /** @type {HTMLAnchorElement | null} */ (target.closest('a[data-billet]'));
    if (ticket) {
      const date = this.#dates[Number(ticket.dataset.billet)];
      if (date) this.#measure(date, ticket.href, ticket.closest('dialog') ? 'fiche' : 'liste');
      return;
    }
    if (target.closest('a')) return; // tickets, the venue's site

    if (target.closest('[data-vers-carte]')) {
      this.#openMap();
      return;
    }

    if (target.closest('[data-effacer]')) {
      this.#clearQuery();
      return;
    }

    if (target.closest('[data-par-date]')) {
      this.#setHere(null);
      return;
    }

    const sheet = target.closest('[data-fiche]');
    if (sheet) {
      this.#openSheet(sheet.getAttribute('data-fiche') ?? '');
      return;
    }

    const zoom = target.closest('[data-zoom]');
    if (zoom && this.#map) {
      if (zoom.getAttribute('data-zoom') === 'in') this.#map.zoomIn();
      else if (zoom.getAttribute('data-zoom') === 'out') this.#map.zoomOut();
      else if (zoom.getAttribute('data-zoom') === 'moi') this.#locateMe(/** @type {HTMLElement} */ (zoom));
      else this.#centerMap('recentrer');
    }
  }

  /* ---------- search ---------- */

  #onInput() {
    this.#syncClear();
    clearTimeout(this.#debounce);
    this.#debounce = window.setTimeout(() => this.#applyQuery(), 140);
  }

  /** @param {{ scroll?: boolean }} [options] - Whether to bring the list into view (Enter). */
  #applyQuery({ scroll = false } = {}) {
    clearTimeout(this.#debounce);
    const query = this.#refs().champ.value.trim();
    if (query !== this.#query) {
      this.#query = query;
      this.#status('');
      this.#replaceAddress();
      this.#render();
      this.#locateIfNeeded();
      // The search stuck over the list: the new list's top back under it, at once (the visitor is typing).
      if (!scroll) this.#toListTop('auto');
    }
    if (scroll) this.#toListTop(motion(), true);
  }

  /**
   * The list's heading just under the stuck search (its scroll margin, in assets/sdm-tournee.css, adds the search's
   * height, measured now). Only when the search is stuck and the heading has gone under it, unless asked anyway.
   * @param {ScrollBehavior} behavior
   * @param {boolean} [always]
   */
  #toListTop(behavior, always = false) {
    const { recherche, resume } = this.#refs();
    if (!always && !this.hasAttribute('data-colle')) return;
    if (!always && resume.getBoundingClientRect().top >= recherche.getBoundingClientRect().bottom - 1) return;
    this.style.setProperty('--st-sous-barre', `${recherche.hidden ? 0 : recherche.offsetHeight}px`);
    resume.scrollIntoView({ behavior, block: 'start' });
  }

  #clearQuery() {
    const { champ } = this.#refs();
    champ.value = '';
    this.#applyQuery();
    this.#syncClear();
    champ.focus();
  }

  #syncClear() {
    const { champ, effacer } = this.#refs();
    if (effacer) effacer.hidden = !champ.value;
  }

  /** @param {string} text */
  #status(text) {
    const { statut } = this.#refs();
    if (statut) statut.textContent = text;
  }

  /**
   * The dates the search matches: each of its words begins a word of the date's venue or day (« lyo », « 69 »,
   * « mar »); a day's number (one or two digits) is a whole word, so that « 20 mars » is not « 2027 ».
   */
  #matches() {
    const words = normalize(this.#query).split(' ').filter(Boolean);
    if (!words.length) return this.#dates;
    return this.#dates.filter((date) => {
      const haystack = this.#haystack(date);
      return words.every((word) => haystack.includes(/^\d{1,2}$/.test(word) ? ` ${word} ` : ` ${word}`));
    });
  }

  /** « paris 12e accor arena 75012 france vendredi 12 mars 2027 », accents and case aside. @param {TourDate} date */
  #haystack(date) {
    let haystack = this.#haystacks.get(date);
    if (haystack === undefined) {
      const venue = this.#venue(date);
      haystack = ` ${normalize([venue.v, townOf(venue), venue.n, venue.p, venue.c || 'France', LONG_DATE_YEAR.format(toDate(date.d))].join(' '))} `;
      this.#haystacks.set(date, haystack);
    }
    return haystack;
  }

  /** The search's town, once found: only for the search it answers. */
  #currentPlace() {
    return this.#placeFor && this.#placeFor === this.#query ? this.#place : null;
  }

  /** A search that matches no date, long enough to be a town's name: the town, by the address API. */
  async #locateIfNeeded() {
    const query = this.#query;
    if (!query || normalize(query).length < 3 || this.#placeFor === query || this.#matches().length) return;
    const place = await this.#fetchTown(query);
    if (place === undefined || query !== this.#query) return; // aborted, or the search has moved on
    this.#place = place;
    this.#placeFor = query;
    this.#render();
  }

  /**
   * The best town for a name or a postcode (France), from the State's address API; null when there is none worth
   * taking (a weak match: « Genève » is not « Genevrey »), undefined when a newer search aborted it.
   * @param {string} query
   * @returns {Promise<Place | null | undefined>}
   */
  async #fetchTown(query) {
    this.#search?.abort();
    const controller = new AbortController();
    this.#search = controller;
    const url = new URL(`${GEOCODER}/search`);
    url.search = new URLSearchParams({ q: query, type: 'municipality', limit: '1' }).toString();
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const { features = [] } = await response.json();
      const feature = features[0];
      if (!feature || !((feature.properties?.score ?? 0) >= MIN_SCORE)) return null;
      return { name: feature.properties.name, lng: feature.geometry.coordinates[0], lat: feature.geometry.coordinates[1] };
    } catch {
      return controller.signal.aborted ? undefined : null;
    }
  }

  /* ---------- rendering ---------- */

  #render() {
    const { liste, recherche, voirCarte } = this.#refs();

    if (!this.#dates.length) {
      const message = this.#hadPast ? this.#texts.passe : this.#texts.vide; // vide : rien
      liste.innerHTML = this.#loading || !message ? '' : `<p class="sdm-tournee__vide">${esc(message)}</p>`;
      recherche.hidden = !this.#loading;
      this.#refs().resume.textContent = this.#loading ? 'Chargement des dates…' : '';
      this.#placeProducts();
      return;
    }

    recherche.hidden = false;
    if (voirCarte) voirCarte.hidden = !this.#mapped().length;

    const matches = this.#matches();
    const place = this.#currentPlace();
    const anchor = this.#anchor(matches);
    if (!this.#query && this.#here) {
      // « Me localiser »: every date from the nearest to the visitor.
      liste.innerHTML = this.#list([{ dates: this.#byDistance(this.#here), from: this.#here }]);
    } else if (anchor) {
      // The town's dates first, then every other date from the nearest.
      const others = this.#byDistance(anchor).filter((date) => !matches.includes(date));
      liste.innerHTML = this.#list([
        { dates: matches },
        { title: 'Les plus proches', dates: others, from: anchor },
      ]);
    } else if (matches.length) {
      liste.innerHTML = this.#list([{ dates: matches, months: true }]);
    } else if (place) {
      liste.innerHTML = this.#list([{ dates: this.#byDistance(place), from: place }]);
    } else if (normalize(this.#query).length >= 3 && this.#placeFor !== this.#query) {
      liste.innerHTML = `<p class="sdm-tournee__vide">Recherche de « ${esc(this.#query)} »…</p>`;
    } else {
      liste.innerHTML = `<div class="sdm-tournee__vide"><p>Aucune date ne correspond à « ${esc(this.#query)} ».</p><button type="button" class="sdm-tournee__plus" data-effacer>Voir toutes les dates</button></div>`;
    }
    this.#placeProducts();
    this.#renderSummary(matches, place, anchor);
  }

  /**
   * A search that names one town of the tour (« lyon », « 69150 », « 20 mars »): the place of its venue, from which
   * the other dates run by distance. None for the whole tour, nor for a search that several towns match (« mars »,
   * « zénith »): the list is then only filtered.
   * @param {TourDate[]} matches
   * @returns {Place | null}
   */
  #anchor(matches) {
    if (!this.#query || !matches.length || matches.length === this.#dates.length) return null;
    const towns = new Set(matches.map((date) => normalize(townOf(this.#venue(date)))));
    if (towns.size !== 1) return null;
    const venue = matches.map((date) => this.#venue(date)).find((one) => one.y !== null && one.x !== null);
    return venue ? { name: townOf(venue), lat: /** @type {number} */ (venue.y), lng: /** @type {number} */ (venue.x) } : null;
  }

  /** Every date, from the nearest to a place (as the crow flies), then in date order. @param {Place} place */
  #byDistance(place) {
    return this.#dates
      .map((date) => ({ date, km: this.#km(place, this.#venue(date)) ?? Infinity }))
      .sort((a, b) => a.km - b.km || compare(a.date.d, b.date.d))
      .map(({ date }) => date);
  }

  /** @param {Place} place @param {Venue} venue */
  #km(place, venue) {
    return venue.y === null || venue.x === null ? null : haversine(place.lat, place.lng, venue.y, venue.x);
  }

  /**
   * The list (HTML), in groups: month by month in date order (`months`), or one run under a title, from a place (the
   * distance on each date); the collection's products after the n-th date, whatever the group.
   * @param {{ title?: string, dates: TourDate[], from?: Place | null, months?: boolean }[]} groups
   */
  #list(groups) {
    const productsAfter = Number(this.#products?.dataset.position) || 0;
    let html = '';
    let open = false;
    let index = 0;
    const close = () => {
      if (open) html += '</ol></div>';
      open = false;
    };
    const start = (/** @type {string} */ title) => {
      close();
      html += `<div class="sdm-tournee__groupe">${title ? `<h2 class="sdm-tournee__mois">${esc(title)}</h2>` : ''}<ol class="sdm-tournee__dates">`;
      open = true;
    };
    for (const { title = '', dates, from = null, months = false } of groups) {
      if (!dates.length) continue;
      let month = '';
      if (!months) start(title);
      for (const date of dates) {
        if (productsAfter && index === productsAfter) {
          close();
          html += '<div data-sdm-produits></div>';
        }
        if (months && date.d.slice(0, 7) !== month) {
          month = date.d.slice(0, 7);
          start(capitalize(MONTH_YEAR.format(toDate(date.d))));
        } else if (!open) {
          start('');
        }
        html += this.#row(date, { from });
        index++;
      }
    }
    close();
    return html;
  }

  /** The products where the list keeps their place, else back under the list. */
  #placeProducts() {
    const { liste } = this.#refs();
    const products = this.#products;
    if (!products) return;
    const slot = liste.querySelector('[data-sdm-produits]');
    if (slot) slot.replaceWith(products);
    else if (products.previousElementSibling !== liste) liste.after(products);
  }

  /**
   * Whether a date can be booked: not when it is full or cancelled, nor postponed without a link (its status is
   * then on the button); else its link, or none yet (« Bientôt »).
   * @param {TourDate} date
   */
  #state(date) {
    if (date.k === 'complet' || date.k === 'annulee') return { off: true, href: '' };
    const href = this.#link(date);
    if (!href && date.k === 'reportee') return { off: true, href: '' };
    return { off: false, href };
  }

  /**
   * A date (HTML): the day, the town (its country abroad) and the venue, its labels, its button. In the list, the
   * whole row opens the venue's sheet (the town is its button for the keyboard); in the sheet, the long date in
   * place of the venue's name.
   * @param {TourDate} date
   * @param {{ from?: Place | null, inSheet?: boolean }} [options]
   */
  #row(date, { from = null, inSheet = false } = {}) {
    const venue = this.#venue(date);
    const day = toDate(date.d);
    const { off, href } = this.#state(date);
    const km = from ? this.#km(from, venue) : null;
    const abroad = venue.c && normalize(venue.c) !== 'france' ? venue.c : '';

    const labels = [
      date.t ? `<span class="sdm-tournee__badge sdm-tournee__badge--accent">${esc(date.t)}</span>` : '',
      date.k && !off ? `<span class="sdm-tournee__badge${date.k === 'dernieres' ? ' sdm-tournee__badge--accent' : ''}">${esc(date.st)}</span>` : '',
    ].join('');

    const when = `<time class="sdm-tournee__quand" datetime="${date.d}"><small>${esc(short(WEEKDAY, day))}</small><strong>${day.getUTCDate()}</strong><small>${esc(short(MONTH, day))}</small></time>`;
    const what = inSheet
      ? `<p class="sdm-tournee__jour-long">${esc(capitalize(LONG_DATE_YEAR.format(day)))}</p>`
      : `<h3 class="sdm-tournee__ville"><button type="button" data-fiche="${esc(venue.id)}" aria-haspopup="dialog">${esc(townOf(venue))}</button>${abroad ? `<span class="sdm-tournee__pays">${esc(abroad)}</span>` : ''}</h3>
        <p class="sdm-tournee__salle">${esc(venue.n)}${km === null ? '' : `<span class="sdm-tournee__km"> · à ${kilometres(km)}</span>`}</p>`;

    return `<li class="sdm-tournee__date${off ? ' sdm-tournee__date--off' : ''}"${inSheet ? '' : ` data-fiche="${esc(venue.id)}"`}>
      ${when}
      <div class="sdm-tournee__infos">${what}${labels ? `<p class="sdm-tournee__badges">${labels}</p>` : ''}</div>
      ${this.#ticket(date, venue, { off, href })}
    </li>`;
  }

  /**
   * « Billets », or the status in its place.
   * @param {TourDate} date @param {Venue} venue @param {{ off: boolean, href: string }} state
   */
  #ticket(date, venue, { off, href }) {
    const what = `${townOf(venue)}, ${venue.n}, ${fullDate(date.d)}`;
    if (off) {
      return `<span class="sdm-tournee__billets sdm-tournee__billets--off" aria-label="${esc(`${date.st} : ${what}`)}">${esc(date.st)}</span>`;
    }
    if (!href) {
      const soon = this.#texts.bientot || 'Bientôt';
      return `<span class="sdm-tournee__billets sdm-tournee__billets--bientot" aria-label="${esc(`${soon} : ${what}`)}">${esc(soon)}</span>`;
    }
    const label = this.#texts.billets || 'Billets';
    return `<a class="sdm-tournee__billets" href="${esc(href)}" target="_blank" rel="noopener" data-billet="${date.i}" aria-label="${esc(`${label} : ${what} (nouvel onglet)`)}"><span>${esc(label)}</span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 6l4 4-4 4"/></svg></a>`;
  }

  /** The date's ticketing link, with UTM parameters. @param {TourDate} date */
  #link(date) {
    const link = date.l;
    if (!link || !this.dataset.utm) return link;

    try {
      const url = new URL(link);
      const [source, medium, campaign] = this.dataset.utm.split('|');
      for (const [name, value] of [
        ['utm_source', source],
        ['utm_medium', medium],
        ['utm_campaign', campaign],
        ['utm_content', `${date.s}-${date.d}`],
      ]) {
        if (value && !url.searchParams.has(name)) url.searchParams.set(name, value);
      }
      return url.href;
    } catch {
      return link;
    }
  }

  /**
   * The list's heading: the tour's months (« Mars – avril 2027 »), the town searched or found, or the search; the
   * count on the right, its number large: « 10 DATES ».
   * @param {TourDate[]} matches
   * @param {Place | null} place
   * @param {Place | null} anchor
   */
  #renderSummary(matches, place, anchor) {
    const { resume } = this.#refs();
    let title = '';
    let under = '';
    let count = matches.length;
    if (!this.#query && this.#here) {
      title = 'Près de vous';
      under = 'Les plus proches d’abord';
    } else if (!this.#query) {
      title = this.#period();
      under = plural(this.#towns(this.#dates), 'ville');
    } else if (anchor) {
      title = anchor.name;
      under = matches.length < this.#dates.length ? 'Puis les dates les plus proches' : '';
    } else if (matches.length) {
      const towns = new Set(matches.map((date) => townOf(this.#venue(date))));
      title = towns.size === 1 ? [...towns][0] : `« ${this.#query} »`;
      under = towns.size === 1 ? '' : plural(towns.size, 'ville');
    } else if (place) {
      title = `Pas de date à ${place.name}`;
      under = 'Les plus proches d’abord';
      count = this.#dates.length;
    } else {
      resume.innerHTML = '';
      return;
    }
    if (this.#loading) under = [under, 'chargement…'].filter(Boolean).join(' · ');
    const byDate = !this.#query && this.#here ? ' · <button type="button" class="sdm-tournee__par-date" data-par-date>Trier par date</button>' : '';
    resume.innerHTML = `<span class="sdm-tournee__resume-quand"><span class="sdm-tournee__resume-jour">${esc(title)}</span>${under ? `<span>${esc(capitalize(under))}${byDate}</span>` : ''}</span><span class="sdm-tournee__compte"><strong>${count}</strong> date${count > 1 ? 's' : ''}</span>`;
  }

  /** « Mars 2027 », « Mars – avril 2027 », « Décembre 2026 – février 2027 ». */
  #period() {
    const first = this.#dates[0]?.d;
    const last = this.#dates[this.#dates.length - 1]?.d;
    if (!first || !last) return '';
    if (first.slice(0, 7) === last.slice(0, 7)) return capitalize(MONTH_YEAR.format(toDate(first)));
    const start = first.slice(0, 4) === last.slice(0, 4) ? MONTH_LONG.format(toDate(first)) : MONTH_YEAR.format(toDate(first));
    return `${capitalize(start)} – ${MONTH_YEAR.format(toDate(last))}`;
  }

  /** @param {TourDate[]} dates */
  #towns(dates) {
    return new Set(dates.map((date) => normalize(townOf(this.#venue(date))))).size;
  }

  /* ---------- the sheet: a venue, or the whole tour on the map ---------- */

  /**
   * A venue's sheet.
   * @param {string} id
   * @param {{ push?: boolean }} [options] - Whether to add a history entry (Back closes the sheet).
   */
  #openSheet(id, { push = true } = {}) {
    if (!this.#venues.has(id) || !this.#dates.some((date) => date.s === id)) return;
    const from = this.#refs().fiche.open ? this.#view : '';
    this.#view = 'fiche';
    this.#sheet = id;
    this.#renderSheet();
    this.#show(push);
    this.#renderMap(from);
  }

  /** The whole tour: every venue on the map, every date in the panel. @param {{ push?: boolean }} [options] */
  #openMap({ push = true } = {}) {
    if (!this.#dates.length) return;
    const from = this.#refs().fiche.open ? this.#view : '';
    this.#view = 'carte';
    this.#sheet = '';
    this.#renderOverview();
    this.#show(push);
    this.#renderMap(from);
  }

  /** @param {boolean} push */
  #show(push) {
    const { fiche, ficheContenu } = this.#refs();
    fiche.dataset.mode = this.#view;
    if (fiche.open) {
      history.replaceState(history.state, '', this.#writeAddress());
      ficheContenu.scrollTo({ top: 0 });
    } else {
      if (push) history.pushState({ sdmTournee: true }, '', this.#writeAddress());
      fiche.showModal();
    }
    /** @type {HTMLElement | null} */ (fiche.querySelector('[data-fermer]'))?.focus();
  }

  #onSheetClose() {
    this.#sheet = '';
    this.#view = '';
    if (history.state?.sdmTournee) history.back();
    else this.#replaceAddress();
  }

  /** The sheet's head (HTML). @param {string} title @param {string[]} lines @param {string} close */
  #head(title, lines, close) {
    const titleId = this.#refs().ficheContenu.dataset.titreId ?? '';
    return `<div class="sdm-tournee__panneau-tete sdm-tournee__fiche-tete">
        <div>
          <h2 id="${esc(titleId)}">${esc(title)}</h2>
          ${lines.filter(Boolean).map((line) => `<p>${line}</p>`).join('')}
        </div>
        <button type="button" class="sdm-tournee__fermer" data-fermer aria-label="${esc(close)}">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>
        </button>
      </div>`;
  }

  #renderSheet() {
    const { ficheContenu } = this.#refs();
    const venue = this.#venues.get(this.#sheet);
    if (!venue) return;

    const place = this.#currentPlace() ?? this.#here;
    const km = place ? this.#km(place, venue) : null;
    const dates = this.#dates.filter((date) => date.s === venue.id);
    const back =
      this.#mapped().length > 1
        ? `<button type="button" class="sdm-tournee__retour" data-vers-carte><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m12 4-6 6 6 6"/></svg>${esc(this.#texts.toutes || 'Toutes les dates')}</button>`
        : '';

    ficheContenu.innerHTML = `
      ${this.#head(venue.n, [this.#where(venue), km === null ? '' : esc(`À ${kilometres(km)} de ${place?.name ?? ''}, à vol d’oiseau`)], 'Fermer la fiche de la salle')}
      ${back}
      <ol class="sdm-tournee__dates sdm-tournee__dates--fiche" aria-label="${esc(`Dates à ${venue.n}`)}">${dates.map((date) => this.#row(date, { inSheet: true })).join('')}</ol>
      ${
        venue.w
          ? `<div class="sdm-tournee__actions"><a class="sdm-tournee__action" href="${esc(venue.w)}" target="_blank" rel="noopener"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M8 4H4v12h12v-4M11 3h6v6M17 3l-8 8"/></svg>Site de la salle</a></div>`
          : ''
      }
      ${this.#nearby(venue)}`;
  }

  /** « PARIS 12E · 8 boulevard de Bercy », the town first and in ink (HTML); the country abroad. @param {Venue} venue */
  #where(venue) {
    const abroad = venue.c && normalize(venue.c) !== 'france' ? venue.c : '';
    const rest = [abroad, venue.a].filter(Boolean).map(esc).join(' · ');
    return [`<strong class="sdm-tournee__commune">${esc(townOf(venue))}</strong>`, rest].filter(Boolean).join(' · ');
  }

  /**
   * The other venues' next dates, nearest first from this one (as the crow flies), folded: the map shows them.
   * @param {Venue} venue
   */
  #nearby(venue) {
    if (venue.y === null || venue.x === null) return '';
    const here = { name: venue.n, lat: venue.y, lng: venue.x };
    const items = this.#mapped()
      .filter((other) => other.id !== venue.id)
      .map((other) => ({ other, km: /** @type {number} */ (this.#km(here, other)) }))
      .sort((a, b) => a.km - b.km)
      .slice(0, NEARBY)
      .map(({ other, km }) => {
        const next = this.#dates.find((date) => date.s === other.id);
        const when = next ? DAY_MONTH.format(toDate(next.d)) : '';
        return `<li><button type="button" class="sdm-tournee__proche" data-fiche="${esc(other.id)}"><strong>${esc(townOf(other))}</strong><span>${esc([when, kilometres(km)].filter(Boolean).join(' · '))}</span></button></li>`;
      })
      .join('');
    if (!items) return '';
    return `<details class="sdm-tournee__proches"><summary>Dates proches<svg class="sdm-tournee__deplier" viewBox="0 0 20 20" aria-hidden="true"><path d="m5 8 5 5 5-5"/></svg></summary><ul>${items}</ul></details>`;
  }

  /** The map's view: every date in the panel, a button each to its venue's sheet. */
  #renderOverview() {
    const { ficheContenu } = this.#refs();
    const count = this.#dates.length;
    const here = this.#here;
    const steps = (here ? this.#byDistance(here) : this.#dates)
      .map((date) => {
        const venue = this.#venue(date);
        const km = here ? this.#km(here, venue) : null;
        const { off } = this.#state(date);
        const status = off || date.k === 'dernieres' ? date.st : '';
        return `<li><button type="button" class="sdm-tournee__etape${off ? ' sdm-tournee__etape--off' : ''}" data-fiche="${esc(venue.id)}">
          <time datetime="${date.d}">${esc(DAY_MONTH.format(toDate(date.d)))}</time>
          <span class="sdm-tournee__etape-lieu"><strong>${esc(townOf(venue))}</strong><span>${esc(venue.n)}${km === null ? '' : ` · ${kilometres(km)}`}</span></span>
          ${status ? `<em>${esc(status)}</em>` : ''}
        </button></li>`;
      })
      .join('');
    ficheContenu.innerHTML = `
      ${this.#head(this.#texts.toutes || 'Toutes les dates', [esc(`${plural(count, 'date')} · ${here ? 'les plus proches d’abord' : plural(this.#towns(this.#dates), 'ville')}`)], 'Fermer la carte')}
      <ol class="sdm-tournee__etapes">${steps}</ol>`;
  }

  /* ---------- map ---------- */

  /**
   * The sheet's map: every venue of the tour a pin; in a venue's sheet, this one in ink with its name, at the
   * map's centre; in the map's view, the whole tour framed. It pans and zooms (no rotation); a pin opens its
   * venue's sheet.
   * @param {string} [from] - The view the sheet comes from, when it was open (else it opens).
   */
  async #renderMap(from = '') {
    const { plan, fiche } = this.#refs();
    if (!plan) return;
    const venue = this.#venues.get(this.#sheet);
    const located = this.#view === 'carte' ? this.#mapped().length > 0 : venue !== undefined && venue.y !== null && venue.x !== null;
    if (this.#mapFailed || !located) {
      plan.hidden = true;
      return;
    }
    plan.hidden = false;
    if (!fiche.open) return;

    // Until the map is drawn the first time, its place shimmers (assets/sdm-tournee.css).
    if (!this.#map) plan.toggleAttribute('data-chargement', true);
    try {
      this.#making ??= this.#makeMap();
      await this.#making;
    } catch {
      this.#mapFailed = true;
      plan.hidden = true;
      plan.removeAttribute('data-chargement');
      return;
    }
    if (!fiche.open) return;
    this.#map.resize();
    this.#syncPins();
    this.#centerMap(from ? 'suivant' : 'ouverture');
  }

  /** MapLibre (a theme asset, loaded once) and the map's style, recoloured for the page's tone. */
  #loadMap() {
    this.#mapLoad ??= Promise.all([
      // @ts-ignore - MapLibre's global
      window.maplibregl
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = this.dataset.carte ?? '';
            script.onload = resolve;
            script.onerror = reject;
            document.head.append(script);
          }),
      fetch(MAP_STYLE).then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json();
      }),
    ]).then(([, style]) => mapStyle(style, this.#palette()));
    return this.#mapLoad;
  }

  async #makeMap() {
    if (!this.dataset.carte) throw new Error('No map script');
    const style = await this.#loadMap();
    const { plan } = this.#refs();
    if (!plan) throw new Error('No map');
    const venue = this.#venues.get(this.#sheet);

    plan.innerHTML = `<div class="sdm-tournee__plan-carte"></div>
      <div class="sdm-tournee__zoom">
        <button type="button" data-zoom="in" aria-label="Zoomer"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 4v12M4 10h12"/></svg></button>
        <button type="button" data-zoom="out" aria-label="Dézoomer"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h12"/></svg></button>
        <button type="button" data-zoom="fit" aria-label="Recentrer la carte"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 7V3h4M13 3h4v4M17 13v4h-4M7 17H3v-4"/></svg></button>${
          'geolocation' in navigator
            ? `
        <button type="button" data-zoom="moi" aria-label="Me localiser"><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="5.5"/><circle cx="10" cy="10" r="1.4"/><path d="M10 1.5v3M10 15.5v3M1.5 10h3M15.5 10h3"/></svg></button>`
            : ''
        }
      </div>
      <p class="sdm-tournee__note" role="status" data-note></p>`;
    // @ts-ignore - MapLibre's global
    const { maplibregl } = window;
    const map = new maplibregl.Map({
      container: /** @type {HTMLElement} */ (plan.firstElementChild),
      style,
      center: venue && venue.y !== null && venue.x !== null ? [venue.x, venue.y] : FRANCE,
      zoom: venue ? MAP_ZOOM : 5,
      minZoom: 3,
      maxZoom: 17,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      attributionControl: { compact: false },
      locale: { 'Map.Title': 'Carte' },
    });
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation();
    // The towns' names, above their squares, where MapLibre finds room (in date order, or the nearest first); a
    // name also opens its venue's sheet. Blank icons the size of the squares, and of this venue's square and name,
    // keep the names off them (the squares themselves are the markers, for the keyboard).
    map.on('load', () => {
      const palette = this.#palette();
      map.addImage('sdm-case', { width: 18, height: 18, data: new Uint8Array(18 * 18 * 4) });
      map.addImage('sdm-case-ici', { width: 34, height: 34, data: new Uint8Array(34 * 34 * 4) });
      map.addSource('sdm-salles', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      const name = {
        'text-field': ['get', 'n'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 10,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.1,
        'text-anchor': 'bottom',
        'text-max-width': 14,
        'icon-allow-overlap': true,
      };
      map.addLayer({
        id: 'sdm-salles-noms',
        type: 'symbol',
        source: 'sdm-salles',
        filter: ['!', ['get', 'ici']],
        layout: {
          ...name,
          'icon-image': 'sdm-case',
          'text-offset': [0, -1.1],
          'text-padding': 4,
          'text-optional': true,
          'symbol-sort-key': ['get', 'o'],
        },
        paint: { 'text-color': palette.ink, 'text-halo-color': palette.land, 'text-halo-width': 1.6 },
      });
      map.addLayer({
        id: 'sdm-salles-ici',
        type: 'symbol',
        source: 'sdm-salles',
        filter: ['get', 'ici'],
        layout: {
          ...name,
          'icon-image': 'sdm-case-ici',
          'text-offset': [0, -1.9],
          'text-padding': 10,
          'text-letter-spacing': 0.14,
          'text-allow-overlap': true,
        },
        paint: { 'text-opacity': 0 },
      });
      map.on('click', 'sdm-salles-noms', (/** @type {any} */ event) => {
        const id = event.features?.[0]?.properties?.id;
        if (id) this.#openSheet(id);
      });
      map.on('mouseenter', 'sdm-salles-noms', () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', 'sdm-salles-noms', () => (map.getCanvas().style.cursor = ''));
      this.#mapNamed = true;
      this.#syncPins();
    });
    map.once('idle', () => plan.removeAttribute('data-chargement'));
    this.#map = map;
    this.#syncHere();
  }

  /** The map's colours: the page's mist, the dark page's black, or its midnight blue. */
  #palette() {
    if (this.dataset.teinte === 'nuit') return MAP_NUIT;
    return this.dataset.ton === 'sombre' ? MAP_DARK : MAP_LIGHT;
  }

  /**
   * The map ready before it is asked for. Once the page has loaded and the browser is idle (not when the visitor
   * saves data, nor on a slow network), MapLibre, the map's style, its sprite and fonts, and the tiles of the whole
   * tour's frame at this screen's size are fetched into the browser's cache (they are kept there for days, the
   * tiles for years), then the map is made, hidden: « Voir la carte » opens on a map drawn at once, instead of
   * downloading some 2 MB and starting MapLibre.
   */
  #warmMap() {
    if (!this.dataset.carte || !this.#mapped().length) return;
    // @ts-ignore - Network Information API (Chromium only)
    const connection = navigator.connection;
    if (connection?.saveData || /2g$/.test(connection?.effectiveType ?? '')) return;
    const start = () => {
      // @ts-ignore - not in Safari: a timeout instead
      const idle = window.requestIdleCallback ?? ((/** @type {() => void} */ callback) => setTimeout(callback, 1500));
      idle(() => this.#prefetchMap().catch(() => {}), { timeout: 6000 });
    };
    if (document.readyState === 'complete') start();
    else addEventListener('load', start, { once: true, signal: this.#abort?.signal });
  }

  async #prefetchMap() {
    const style = await this.#loadMap();
    if (this.#map || !this.isConnected) return;
    const signal = this.#abort?.signal;
    // As MapLibre asks for them (`credentials: 'same-origin'`): the browser's cache keeps requests apart by their
    // credentials mode, and an `omit` copy would not serve the map.
    const get = (/** @type {string} */ url) => fetch(url, { credentials: 'same-origin', signal }).catch(() => null);

    const ratio = devicePixelRatio > 1 ? '@2x' : '';
    if (typeof style.sprite === 'string') [`${style.sprite}${ratio}.json`, `${style.sprite}${ratio}.png`].forEach(get);
    if (typeof style.glyphs === 'string') {
      for (const [font, range] of GLYPHS) get(style.glyphs.replace('{fontstack}', encodeURIComponent(font)).replace('{range}', range));
    }

    const source = /** @type {any} */ (Object.values(style.sources ?? {}).find((one) => /** @type {any} */ (one).type === 'vector'));
    let template = source?.tiles?.[0];
    if (!template && source?.url) template = (await (await get(source.url))?.json())?.tiles?.[0];
    if (typeof template !== 'string') return;
    for (const [z, x, y] of frameTiles(this.#mapped(), planSize())) {
      get(template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y)));
    }

    // The map itself, made now in its hidden place (MapLibre's workers, its style, the pins): opening it then only
    // draws it. A failure is caught again where the map is shown.
    if (!this.#refs().fiche.open && !this.#making) {
      this.#making = this.#makeMap();
      this.#making.catch(() => {});
    }
  }

  /** A pin per venue of the tour; in a venue's sheet, this one in ink, with its name. */
  #syncPins() {
    if (!this.#map) return;
    // @ts-ignore - MapLibre's global
    const { maplibregl } = window;
    const current = this.#view === 'fiche' ? this.#venues.get(this.#sheet) : undefined;
    const order = new Map(this.#dates.map((date, index) => [date.s, index]).reverse());
    /** @type {object[]} */
    const names = [];

    for (const venue of this.#mapped()) {
      let pin = this.#pins.get(venue.id);
      if (!pin) {
        const element = document.createElement('button');
        element.type = 'button';
        element.className = 'sdm-tournee__epingle';
        element.dataset.fiche = venue.id;
        element.innerHTML = `<span>${esc(townOf(venue))}</span>`;
        pin = new maplibregl.Marker({ element }).setLngLat([venue.x, venue.y]).addTo(this.#map);
        this.#pins.set(venue.id, pin);
      }
      const element = pin.getElement();
      const here = venue.id === current?.id;
      const first = this.#dates.find((date) => date.s === venue.id);
      const when = first ? `, ${fullDate(first.d)}` : '';
      element.classList.toggle('sdm-tournee__epingle--ici', here);
      element.setAttribute('aria-label', `${townOf(venue)}, ${venue.n}${here ? ', cette salle' : when}`);
      // The names' priority: the nearest first, from this venue or from the visitor; else in date order.
      const from = current && current.y !== null && current.x !== null ? { lat: current.y, lng: current.x } : this.#here;
      const rank = from
        ? Math.round(haversine(from.lat, from.lng, /** @type {number} */ (venue.y), /** @type {number} */ (venue.x)))
        : (order.get(venue.id) ?? 0);
      names.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [venue.x, venue.y] },
        properties: { id: venue.id, n: townOf(venue), o: rank, ici: here },
      });
    }
    // Pins of venues that have no date left (a page of data replaced): off the map.
    for (const [id, pin] of this.#pins) {
      if (!names.some((feature) => /** @type {any} */ (feature).properties.id === id)) {
        pin.remove();
        this.#pins.delete(id);
      }
    }
    if (this.#mapNamed) this.#map.getSource('sdm-salles')?.setData({ type: 'FeatureCollection', features: names });
  }

  /**
   * The map's frame: the whole tour in the map's view; a venue at the centre in its sheet, at the same zoom
   * whatever the venue (MAP_ZOOM: its quarter). From one view to another the map flies (it jumps when motion is
   * reduced). « Recentrer » goes back to the view's frame.
   * @param {'ouverture' | 'suivant' | 'recentrer'} how
   */
  #centerMap(how) {
    const map = this.#map;
    if (!map) return;
    const still = how === 'ouverture' || matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.#view === 'carte') {
      // @ts-ignore - MapLibre's global
      const bounds = new window.maplibregl.LngLatBounds();
      for (const venue of this.#mapped()) bounds.extend([venue.x, venue.y]);
      if (bounds.isEmpty()) return;
      const { clientWidth: width, clientHeight: height } = map.getContainer();
      const padding = framePadding(width, height);
      map.fitBounds(bounds, { padding, maxZoom: 9, duration: still ? 0 : 1100 });
      return;
    }

    const venue = this.#venues.get(this.#sheet);
    if (!venue || venue.y === null || venue.x === null) return;
    const center = [venue.x, venue.y];
    if (still) map.jumpTo({ center, zoom: MAP_ZOOM });
    else if (how === 'recentrer') map.easeTo({ center, zoom: MAP_ZOOM, duration: 600 });
    else map.flyTo({ center, zoom: MAP_ZOOM, speed: 1.6, curve: 1.42 });
  }

  /* ---------- the visitor's position ---------- */

  /**
   * « Me localiser »: the browser asks the visitor for their position (then remembers their answer); the dates run
   * from the nearest, and the map frames the visitor with the nearest venues, or with this venue in its sheet.
   * @param {HTMLElement} button
   */
  #locateMe(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    const done = () => button.removeAttribute('aria-busy');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        done();
        if (!this.isConnected) return;
        this.#setHere({ name: 'vous', lat: coords.latitude, lng: coords.longitude });
        this.#frameHere();
      },
      (error) => {
        done();
        this.#mapNote(
          error.code === error.PERMISSION_DENIED
            ? 'Localisation refusée : autorisez-la pour ce site dans les réglages du navigateur.'
            : 'Votre position est introuvable pour le moment.'
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  }

  /**
   * The visitor's position, or none (« Trier par date »): the list, the open sheet, the pins and the dot follow.
   * @param {Place | null} here
   */
  #setHere(here) {
    this.#here = here;
    this.#render();
    if (this.#refs().fiche.open) {
      if (this.#view === 'carte') this.#renderOverview();
      else this.#renderSheet();
    }
    this.#syncPins();
    this.#syncHere();
  }

  /** The visitor's dot on the map, and the crosshair in its colour once they are located. */
  #syncHere() {
    if (!this.#map) return;
    const here = this.#here;
    this.#refs().plan?.querySelector('[data-zoom="moi"]')?.toggleAttribute('data-actif', Boolean(here));
    if (!here) {
      this.#hereMarker?.remove();
      this.#hereMarker = null;
      return;
    }
    if (!this.#hereMarker) {
      const element = document.createElement('span');
      element.className = 'sdm-tournee__vous';
      element.setAttribute('role', 'img');
      element.setAttribute('aria-label', 'Votre position');
      // @ts-ignore - MapLibre's global
      this.#hereMarker = new window.maplibregl.Marker({ element });
    }
    this.#hereMarker.setLngLat([here.lng, here.lat]).addTo(this.#map);
  }

  /** The visitor and what is near them, framed: this venue in its sheet, else the three nearest venues. */
  #frameHere() {
    const map = this.#map;
    const here = this.#here;
    if (!map || !here) return;
    const venue = this.#view === 'fiche' ? this.#venues.get(this.#sheet) : undefined;
    const near =
      venue && venue.y !== null && venue.x !== null
        ? [venue]
        : this.#mapped()
            .map((one) => ({ one, km: /** @type {number} */ (this.#km(here, one)) }))
            .sort((a, b) => a.km - b.km)
            .slice(0, 3)
            .map(({ one }) => one);
    // @ts-ignore - MapLibre's global
    const bounds = new window.maplibregl.LngLatBounds([here.lng, here.lat], [here.lng, here.lat]);
    for (const one of near) bounds.extend([one.x, one.y]);
    const { clientWidth: width, clientHeight: height } = map.getContainer();
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
    map.fitBounds(bounds, { padding: framePadding(width, height) + 16, maxZoom: MAP_ZOOM, duration: still ? 0 : 1100 });
  }

  /** A short note on the map (a position refused or not found), for a few seconds. @param {string} text */
  #mapNote(text) {
    const note = /** @type {HTMLElement | null} */ (this.#refs().plan?.querySelector('[data-note]'));
    if (!note) return;
    note.textContent = text;
    clearTimeout(this.#noteTimer);
    this.#noteTimer = window.setTimeout(() => (note.textContent = ''), 6000);
  }

  /* ---------- glass ---------- */

  /** The sheet and the bubble, in the floating menu's glass: sdm/glass adds the refraction on Chromium desktop. */
  async #initGlass() {
    const { defs } = this.#refs();
    if (!defs) return;
    try {
      // @ts-ignore - resolved by the theme's import map (snippets/sdm-importmap.liquid)
      const { createGlass, GLASS_DEFAULTS } = await import('sdm/glass');
      if (!this.isConnected) return;
      const glass = createGlass({ defs, params: { ...structuredClone(GLASS_DEFAULTS), ...GLASS } });
      this.querySelectorAll('[data-verre]').forEach((element) => glass.register(element));
    } catch {
      // No sdm/glass in the import map: the CSS blur stays.
    }
  }

  /* ---------- products ---------- */

  /** The products' videos, as on the home page's islands: loaded in view, playing while on screen. */
  #watchVideos() {
    const videos = this.querySelectorAll('video[data-sdm-tournee-video]');
    if (!videos.length) return;
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const { target, isIntersecting } of entries) {
          const video = /** @type {HTMLVideoElement} */ (target);
          if (!isIntersecting) {
            video.pause();
            continue;
          }
          if (video.preload === 'none') video.preload = 'metadata';
          if (!still) video.play().catch(() => {});
        }
      },
      { rootMargin: '200px 0px' }
    );
    videos.forEach((video) => observer.observe(video));
    this.#observers.push(observer);
  }

  /* ---------- measure ---------- */

  /** @param {TourDate} date @param {string} href @param {'liste' | 'fiche'} origin */
  #measure(date, href, origin) {
    const venue = this.#venue(date);
    const detail = {
      tournee: this.dataset.tournee ?? '',
      titre: this.dataset.titre ?? '',
      salle: venue.n,
      salle_id: venue.id,
      ville: venue.v,
      pays: venue.c || 'France',
      date: date.d,
      statut: date.st || 'En vente',
      mention: date.t,
      lien: href,
      recherche: this.#query ? (this.#currentPlace() ? 'ville_sans_date' : 'ville') : this.#here ? 'position' : 'aucune',
      origine: origin,
    };

    try {
      // @ts-ignore - Shopify's customer events (web pixels), on every storefront page
      window.Shopify?.analytics?.publish?.('sdm_tournee_billetterie', detail);
    } catch {
      // the link still opens
    }
    // @ts-ignore - Google Tag Manager, when a container is on the page
    window.dataLayer?.push?.({ event: 'sdm_tournee_billetterie', ...detail });
    this.dispatchEvent(new CustomEvent('sdm-tournee:billetterie', { bubbles: true, detail }));
  }
}

/** The section's JSON. */
const DATA = 'script[data-sdm-tournee-donnees]';

/** IGN Géoplateforme, which serves the State's address API (api-adresse.data.gouv.fr has moved there). */
const GEOCODER = 'https://data.geopf.fr/geocodage';

/** Below this score, the address API's best town is not taken (« Geneve » gives « Genevrey » at 0.43). */
const MIN_SCORE = 0.6;

/** Dates in the sheet's « Dates proches ». */
const NEARBY = 4;

/** A venue's sheet's map zoom: its quarter. */
const MAP_ZOOM = 12;

/** The map's centre before the whole tour is framed. */
const FRANCE = [2.4, 46.6];

/** OpenFreeMap's Positron (OpenStreetMap's data, OpenMapTiles' schema): free, no key, no cookie. */
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';

/** The map's colours: the page's mist and night ink, or the dark page's. */
const MAP_LIGHT = {
  land: '#eef3f6',
  park: '#e5edf0',
  water: '#d3dee4',
  building: '#e3eaee',
  road: '#ffffff',
  line: '#dbe3e8',
  boundary: '#b9c5cc',
  label: '#6d7c84',
  ink: '#2b3a42',
};
const MAP_DARK = {
  land: '#0b0f12',
  park: '#0e1519',
  water: '#05080a',
  building: '#121a1f',
  road: '#1b252b',
  line: '#141c21',
  boundary: '#2c3840',
  label: '#7f8e96',
  ink: '#d5dde1',
};

/** The midnight-blue page's map (`data-teinte="nuit"`): the page's blue, a shade lighter for the land. */
const MAP_NUIT = {
  land: '#0f1f2b',
  park: '#122431',
  water: '#08141d',
  building: '#152937',
  road: '#1d3445',
  line: '#172c3a',
  boundary: '#31495a',
  label: '#7f96a6',
  ink: '#dce6ed',
};

/** The map's fonts and their ranges, as Positron asks for them over France (prefetched with the tiles). */
const GLYPHS = [
  ['Noto Sans Regular', '0-255'],
  ['Noto Sans Regular', '256-511'],
  ['Noto Sans Italic', '0-255'],
  ['Noto Sans Bold', '0-255'],
];

/** MapLibre's world: 512 px tiles. */
const TILE = 512;

/** The floating menu's glass (assets/sdm-burger.js), so the panels read as the same material. */
const GLASS = { bezel: 18, depth: 40, chroma: 0.18, blur: 0.8, tint: 0.05, rim: 0.7, spec: 0.28 };

/** Towns with arrondissements: their postcodes give it (75012 → Paris 12e). */
const ARRONDISSEMENTS = { Paris: ['75', 20], Lyon: ['69', 9], Marseille: ['13', 16] };

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const WEEKDAY = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', timeZone: 'UTC' });
const MONTH = new Intl.DateTimeFormat('fr-FR', { month: 'short', timeZone: 'UTC' });
const MONTH_LONG = new Intl.DateTimeFormat('fr-FR', { month: 'long', timeZone: 'UTC' });
const MONTH_YEAR = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const DAY_MONTH = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const LONG_DATE_YEAR = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const PARIS = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' });
const DECIMAL = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
const COLLATOR = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

/** @param {string | null | undefined} text */
function parse(text) {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

/** @param {unknown} value */
function number(value) {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : null;
}

/** @param {{ s: string, d: string }} date */
function key(date) {
  return `${date.s}|${date.d}`;
}

/**
 * The status's kind, from the metaobject's choice (« Dernières places », « Complet », « Reportée », « Annulée »).
 * @param {string} label
 */
function statusKind(label) {
  const text = normalize(label);
  if (!text) return '';
  if (text.startsWith('complet')) return 'complet';
  if (text.startsWith('annul')) return 'annulee';
  if (text.startsWith('report')) return 'reportee';
  if (text.startsWith('dernier')) return 'dernieres';
  return 'autre';
}

/** Today in Paris (YYYY-MM-DD), whatever the visitor's time zone. */
function parisToday() {
  const parts = Object.fromEntries(PARIS.formatToParts(new Date()).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** @param {string} date - YYYY-MM-DD */
function toDate(date) {
  return new Date(`${date}T12:00:00Z`);
}

/** « ven », « mars », « avr » (no dot). @param {Intl.DateTimeFormat} format @param {Date} day */
function short(format, day) {
  return format.format(day).replace(/\.$/, '');
}

/** « vendredi 12 mars 2027 ». @param {string} date */
function fullDate(date) {
  return LONG_DATE_YEAR.format(toDate(date));
}

/** @param {string} text */
function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** « 1 date », « 10 dates ». @param {number} count @param {string} word */
function plural(count, word) {
  return `${count} ${word}${count > 1 ? 's' : ''}`;
}

/** « Paris 12e », « Lyon 7e », else the town. @param {Venue} venue */
function townOf(venue) {
  const [prefix, count] = ARRONDISSEMENTS[/** @type {keyof typeof ARRONDISSEMENTS} */ (venue.v)] ?? [];
  const arrondissement = Number(venue.p?.slice(3));
  if (prefix && venue.p?.startsWith(`${prefix}0`) && arrondissement >= 1 && arrondissement <= Number(count)) {
    return `${venue.v} ${arrondissement}${arrondissement === 1 ? 'er' : 'e'}`;
  }
  return venue.v;
}

/** Lower case, no accents, words only: « Saint-Étienne » → « saint etienne ». @param {unknown} text */
function normalize(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * The map's style, recoloured: land, water, parks and buildings in the palette's tints, roads in white (or
 * dark), labels in grey and ink, names in French; no shields, airports, railways' dashes or country names.
 * @param {any} style
 * @param {typeof MAP_LIGHT} palette
 */
function mapStyle(style, palette) {
  const dropped = /shield|airport|aeroway|dashline|label_(country|state)|ice|glacier/;
  style.layers = style.layers.filter((/** @type {any} */ layer) => !dropped.test(layer.id));
  for (const layer of style.layers) {
    const { id, type } = layer;
    const paint = (layer.paint ??= {});
    if (type === 'background') {
      paint['background-color'] = palette.land;
    } else if (type === 'fill') {
      paint['fill-color'] = /water/.test(id)
        ? palette.water
        : /park|wood/.test(id)
          ? palette.park
          : /building/.test(id)
            ? palette.building
            : palette.land;
      if ('fill-outline-color' in paint) paint['fill-outline-color'] = palette.line;
    } else if (type === 'line') {
      paint['line-color'] = /water/.test(id)
        ? palette.water
        : /boundary/.test(id)
          ? palette.boundary
          : /casing|railway/.test(id)
            ? palette.line
            : palette.road;
    } else if (type === 'symbol') {
      paint['text-color'] = /city|town/.test(id) ? palette.ink : palette.label;
      paint['text-halo-color'] = palette.land;
      if (layer.layout?.['text-field']) layer.layout['text-field'] = ['coalesce', ['get', 'name:fr'], ['get', 'name']];
    }
  }
  return style;
}

/** Great-circle distance in km. @param {number} lat1 @param {number} lng1 @param {number} lat2 @param {number} lng2 */
function haversine(lat1, lng1, lat2, lng2) {
  const rad = Math.PI / 180;
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lng2 - lng1) * rad) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(a));
}

/** The margin around the whole tour on the map: an eighth of its smaller side, 24 to 64 px. */
function framePadding(/** @type {number} */ width, /** @type {number} */ height) {
  return Math.max(24, Math.min(64, Math.round(Math.min(width, height) / 8)));
}

/**
 * The size the sheet's map will have in the map's view, from assets/sdm-tournee.css: at
 * the left of the 460 px panel on a computer; above it elsewhere, the panel taking 46 % of the height (440 px at most).
 */
function planSize() {
  const width = document.documentElement.clientWidth || innerWidth;
  const height = innerHeight;
  if (width >= 990) return { width: Math.min(1040, width - 32) - 460 - 16, height: height - 32 };
  const box = width >= 750 ? { width: Math.min(520, width - 48), height: height - 48 } : { width: width - 24, height: height - 24 };
  return { width: box.width, height: box.height - 8 - Math.min(box.height * 0.46, 440) };
}

/**
 * The tiles MapLibre will ask for to draw the whole tour, framed as `fitBounds` frames it (Web Mercator, 512 px
 * tiles at the zoom's floor, as for any vector source).
 * @param {Venue[]} venues
 * @param {{ width: number, height: number }} size
 * @returns {[number, number, number][]}
 */
function frameTiles(venues, { width, height }) {
  if (!venues.length || width <= 0 || height <= 0) return [];
  const xs = venues.map((venue) => (/** @type {number} */ (venue.x) + 180) / 360);
  const ys = venues.map((venue) => {
    const sin = Math.sin((/** @type {number} */ (venue.y) * Math.PI) / 180);
    return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
  });
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const padding = framePadding(width, height);
  const zoom = Math.min(
    9,
    Math.log2((width - 2 * padding) / (TILE * Math.max(maxX - minX, 1e-9))),
    Math.log2((height - 2 * padding) / (TILE * Math.max(maxY - minY, 1e-9)))
  );
  const z = Math.max(0, Math.floor(zoom));
  const count = 2 ** z;
  const world = TILE * 2 ** zoom;
  const [cx, cy] = [(minX + maxX) / 2, (minY + maxY) / 2];
  const [halfX, halfY] = [width / 2 / world, height / 2 / world];
  /** @type {[number, number, number][]} */
  const tiles = [];
  for (let x = Math.floor((cx - halfX) * count); x <= Math.floor((cx + halfX) * count); x++) {
    for (let y = Math.max(0, Math.floor((cy - halfY) * count)); y <= Math.min(count - 1, Math.floor((cy + halfY) * count)); y++) {
      tiles.push([z, ((x % count) + count) % count, y]);
    }
  }
  return tiles;
}

/** « 850 m », « 2,4 km », « 95 km ». @param {number} km */
function kilometres(km) {
  if (km < 1) return `${Math.max(10, Math.round((km * 1000) / 10) * 10)} m`;
  if (km < 10) return `${DECIMAL.format(km)} km`;
  return `${Math.round(km)} km`;
}

/** @param {string} a @param {string} b */
function compare(a, b) {
  return COLLATOR.compare(a ?? '', b ?? '');
}

/** @returns {ScrollBehavior} */
function motion() {
  return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

/**
 * The tone of the first ground painted under a point, the floating menu and an element aside: its background
 * colour, when it is opaque enough; dark below the floating menu's threshold (assets/sdm-burger.js).
 * @param {number} x
 * @param {number} y
 * @param {Element} aside
 * @returns {'clair' | 'sombre' | null}
 */
function toneAt(x, y, aside) {
  for (const element of document.elementsFromPoint(x, y)) {
    if (aside.contains(element) || element.closest('sdm-burger')) continue;
    const value = getComputedStyle(element).backgroundColor;
    const [r = 0, g = 0, b = 0, alpha = 1] = (value.match(/[\d.]+/g) ?? []).map(Number);
    if (alpha < 0.5) continue;
    const scale = value.startsWith('color(') ? 1 : 255;
    const linear = (/** @type {number} */ c) => ((c /= scale) <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b) < 0.23 ? 'sombre' : 'clair';
  }
  return null;
}

/** @param {unknown} value */
function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

if (!customElements.get('sdm-tournee')) {
  customElements.define('sdm-tournee', SdmTournee);
}
